import React from 'react';
import { motion } from 'framer-motion';
import { PlayCircle, ShieldCheck, Check } from 'lucide-react';
import { normalizeEmbedUrl } from '../../lib/embed';
import { Highlight, CtaButton, fadeUp, stagger } from './shared';

function BoldText({ text }) {
  const parts = String(text || '').split(/(\*[^*]+\*)/g).filter(Boolean);
  return parts.map((p, i) => (p.startsWith('*') && p.endsWith('*') && p.length > 2
    ? <strong key={i} className="text-slate-900 font-extrabold">{p.slice(1, -1)}</strong>
    : <React.Fragment key={i}>{p}</React.Fragment>));
}

function VideoFrame({ url, poster, length, badges }) {
  const embed = normalizeEmbedUrl(url);
  return (
    <div className="relative">
      <div className="relative rounded-[18px] sm:rounded-[22px] bg-slate-900 p-1 sm:p-1.5 shadow-[0_45px_110px_-35px_rgba(15,23,42,0.6)]">
        <div className="relative w-full overflow-hidden rounded-[14px] sm:rounded-[17px] bg-slate-950" style={{ aspectRatio: '16 / 9' }}>
          {embed ? (
            <iframe src={embed} title="How CELPIPMax works" loading="lazy" allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              className="absolute inset-0 w-full h-full outline-none focus:outline-none" style={{ outline: 'none' }} data-testid="hero-video" />
          ) : (
            <div className="absolute inset-0" data-testid="hero-video-empty">
              {poster && <img src={poster} alt="CELPIPMax platform" className="absolute inset-0 w-full h-full object-cover object-top opacity-60" />}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
                <span className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/15 backdrop-blur flex items-center justify-center border border-white/30 shadow-2xl">
                  <PlayCircle size={46} className="text-white" />
                </span>
                <p className="text-[13px] font-semibold text-white/80">Walkthrough video coming soon</p>
              </div>
            </div>
          )}
          {length && !embed && (
            <span className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900/80 backdrop-blur text-white text-[12px] font-bold" data-testid="hero-video-length">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />{length}
            </span>
          )}
        </div>
      </div>
      {(badges || []).slice(0, 2).map((b, i) => b && (
        <div key={i} className={`${i === 0 ? 'cmx-float -left-3 lg:-left-28 top-[22%]' : 'cmx-float-slow -right-3 lg:-right-28 top-[58%]'} absolute hidden md:flex items-center gap-2 rounded-xl bg-white shadow-xl border border-neutral-200 px-4 py-2.5`}
          style={{ '--tilt': i === 0 ? '-3deg' : '3deg', animationDelay: `${i * 1.1}s` }} data-testid={`hero-video-badge-${i}`}>
          <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><Check size={13} /></span>
          <span className="text-[12.5px] font-bold text-slate-800">{b}</span>
        </div>
      ))}
    </div>
  );
}

export default function Hero({ hero, proof, videoUrl, onCta, onSecondary, heroRef }) {
  const avatars = (hero.proof_avatars || []).filter(Boolean).slice(0, 6);
  return (
    <section ref={heroRef} id="top" className="relative overflow-hidden" data-testid="hero-section"
      style={{ background: 'linear-gradient(180deg,#e7f0fd 0%,#f3f0e9 62%,#f5f1eb 100%)' }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.35]" aria-hidden="true"
        style={{ backgroundImage: 'linear-gradient(rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.06) 1px, transparent 1px)', backgroundSize: '56px 56px', maskImage: 'radial-gradient(ellipse at 50% 0%, black 30%, transparent 75%)', WebkitMaskImage: 'radial-gradient(ellipse at 50% 0%, black 30%, transparent 75%)' }} />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-[calc(var(--lp-top,72px)+0.75rem)] sm:pt-[calc(var(--lp-top,72px)+1.5rem)] text-center">
        <motion.div variants={stagger} initial="hidden" animate="show">
          {proof && (
            <motion.div variants={fadeUp} className="flex justify-center mb-4 sm:mb-5">
              <span className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/80 backdrop-blur border border-white shadow-sm text-[12.5px] sm:text-[13px] font-semibold text-slate-700" data-testid="social-proof-pill">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                {proof.text}
              </span>
            </motion.div>
          )}
          <motion.h1 variants={fadeUp} className="font-[Manrope] font-extrabold text-[2.1rem] leading-[1.06] sm:text-5xl lg:text-[3.4rem] tracking-[-0.035em] text-slate-900 mb-3.5 sm:mb-5" data-testid="hero-title">
            <Highlight text={hero.headline} />
          </motion.h1>
          <motion.p variants={fadeUp} className="text-[15px] sm:text-[17px] text-slate-600 max-w-3xl mx-auto leading-relaxed" data-testid="hero-subtitle">
            {hero.subtitle}
          </motion.p>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-[690px] mx-auto px-4 sm:px-6 mt-5 sm:mt-7" data-testid="hero-vsl">
        <VideoFrame url={videoUrl} poster={hero.product_image} length={hero.video_length} badges={hero.video_badges} />
        {hero.video_caption && <p className="text-center text-[12.5px] sm:text-sm text-slate-500 mt-3" data-testid="hero-video-caption">{hero.video_caption}</p>}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }}
        className="relative max-w-3xl mx-auto px-4 sm:px-6 mt-5 sm:mt-6 pb-10 sm:pb-14 text-center">
        <CtaButton label={hero.cta_label || 'Get Instant Access'} onClick={() => onCta('landing_hero')} variant="blue" className="w-full sm:w-auto" testId="hero-cta" />
        {hero.secondary_text && (
          <div className="mt-3">
            <button onClick={() => onSecondary('landing_hero')} className="text-[13px] text-slate-600 hover:text-slate-900 underline underline-offset-4 decoration-slate-300 hover:decoration-slate-600 transition" data-testid="hero-secondary-cta">
              {hero.secondary_text}
            </button>
          </div>
        )}
        <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-4" data-testid="hero-proof-row">
          {avatars.length > 0 && (
            <div className="flex -space-x-3" data-testid="hero-proof-avatars">
              {avatars.map((src, i) => (
                <img key={i} src={src} alt="" loading="lazy" className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover ring-[3px] ring-[#f5f1eb] shadow-sm" style={{ zIndex: avatars.length - i }} />
              ))}
            </div>
          )}
          <p className="text-[14px] text-slate-600" data-testid="hero-proof-text"><BoldText text={hero.proof_text} /></p>
          {hero.guarantee_chip && (
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/70 px-3 py-1 rounded-full" data-testid="hero-guarantee-chip">
              <ShieldCheck size={13} />{hero.guarantee_chip}
            </span>
          )}
        </div>
      </motion.div>
    </section>
  );
}
